import { useEffect, useRef } from "react";

// Lightweight canvas ember/spark effect for the hero forge.
export default function Embers() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let w = 0;
    let h = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      const parent = canvas.parentElement;
      w = parent.clientWidth;
      h = parent.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const COLORS = ["#FFB04A", "#E8954A", "#FF7A1A", "#FFD27A", "#FF5E2B"];
    const count = Math.min(120, Math.floor(w / 12));
    const particles = [];

    const spawn = (initial = false) => {
      // Bias spark origin toward the right/center where the forge glow sits.
      const originX = w * (0.35 + Math.random() * 0.6);
      return {
        x: originX,
        y: initial ? Math.random() * h : h + Math.random() * 40,
        vx: (Math.random() - 0.5) * 0.5,
        vy: -(0.4 + Math.random() * 1.4),
        size: 0.6 + Math.random() * 2.2,
        life: 0,
        ttl: 120 + Math.random() * 220,
        color: COLORS[(Math.random() * COLORS.length) | 0],
        flicker: Math.random() * Math.PI * 2,
      };
    };

    for (let i = 0; i < count; i++) particles.push(spawn(true));

    const tick = () => {
      ctx.clearRect(0, 0, w, h);
      ctx.globalCompositeOperation = "lighter";
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.life++;
        p.x += p.vx;
        p.y += p.vy;
        p.vy -= 0.004; // hot air lift accelerates upward slightly
        p.vx += (Math.random() - 0.5) * 0.06; // wander
        p.flicker += 0.2;

        const lifeRatio = p.life / p.ttl;
        const alpha = Math.sin(Math.min(lifeRatio, 1) * Math.PI) * (0.6 + 0.4 * Math.sin(p.flicker));

        if (p.life >= p.ttl || p.y < -10) {
          particles[i] = spawn();
          continue;
        }

        const r = p.size;
        const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r * 4);
        grad.addColorStop(0, p.color);
        grad.addColorStop(1, "rgba(0,0,0,0)");
        ctx.globalAlpha = Math.max(0, alpha);
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r * 4, 0, Math.PI * 2);
        ctx.fill();

        // bright core
        ctx.globalAlpha = Math.max(0, alpha * 0.9);
        ctx.fillStyle = "#FFE0A8";
        ctx.beginPath();
        ctx.arc(p.x, p.y, r * 0.6, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = "source-over";
      raf = requestAnimationFrame(tick);
    };
    tick();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="hero-embers"
      aria-hidden="true"
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ zIndex: 5 }}
    />
  );
}
